import { useState } from "react";
import { Badge, Button, Card, Icon } from "../../ds/DS";
import type { Tone } from "../../ds/DS";
import { fmtCost, fmtDay, isOpen } from "../data";
import { buildTodo, type TodoItem } from "../todo";
import { id8 } from "../ticketThread";
import type { PageProps } from "../Portal";
import { PageHead, Progress, ProjectCard, ScopeNote, StatCard } from "./parts";
import {
  activityDay,
  activityWhen,
  currentRelease,
  firstName,
  plural,
  projectMilestones,
} from "./util";
import { ClientEditDialog, ProjectEditDialog } from "./EditDialogs";
import { NewTicket } from "./ticketParts";

interface NeedRow {
  key: string;
  icon: string;
  iconTone: "teal" | "warning";
  title: string;
  meta: string;
  tag: string;
  tone: Tone;
  go: () => void;
}

export function Overview(props: PageProps) {
  return props.isAdmin ? <AdminOverview {...props} /> : <ClientHome {...props} />;
}

/* ---------- admin ---------- */

function AdminOverview(props: PageProps) {
  const {
    client,
    projects,
    scopedProjects,
    tickets,
    tasks,
    releases,
    changeRequests,
    activity,
    pid,
    setPid,
    go,
    openItem,
    reload,
    toast,
    projectName,
  } = props;
  const [editClient, setEditClient] = useState(false);
  const [editProject, setEditProject] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const inProject = pid !== "all";
  const inScope = (projectId: string | null) => !inProject || projectId === pid;
  const openT = tickets.filter((t) => inScope(t.projectId) && isOpen(t));
  const needsReply = openT.filter((t) => t.needsReply);
  const scopedReleases = releases.filter((r) => inScope(r.projectId) && !r.isFixes);
  const inFlight = scopedReleases.filter((r) => r.status === "Open" || r.status === "On sandbox");
  const pendingRel = scopedReleases.filter(
    (r) => r.status === "On sandbox" && r.requiresSignoff && r.signoff === "pending",
  );
  const scopedTasks = tasks.filter((t) => inScope(t.projectId));
  const withClient = scopedTasks.filter((t) => t.state === "Requested");
  const overdue = withClient.filter((t) => t.overdue);
  const pendCR = changeRequests.filter(
    (c) => inScope(c.projectId) && c.state === "Awaiting approval",
  );
  const pendTotal = pendCR.reduce((n, c) => n + (c.costPence ?? 0), 0);
  const contact = client?.contact ? firstName(client.contact) : "the client";
  const scopeName = (projectId: string | null) =>
    projectId ? projectName(projectId) : "Client account";

  const needs: NeedRow[] = [
    ...needsReply.map((t) => {
      const late = t.clock.tone === "danger";
      return {
        key: `t${t.id}`,
        icon: "reply",
        iconTone: "teal" as const,
        title: t.subject,
        meta: [`#${id8(t.id)}`, projectName(t.projectId), t.clock.label].filter(Boolean).join(" · "),
        tag: late ? "Overdue" : "Needs reply",
        tone: (late ? "danger" : "teal") as Tone,
        go: () => openItem("tickets", t.id),
      };
    }),
    ...scopedTasks
      .filter((t) => t.state === "Submitted")
      .map((t) => ({
        key: `r${t.id}`,
        icon: "clipboard-check",
        iconTone: "teal" as const,
        title: `Review: ${t.title}`,
        meta: `${scopeName(t.projectId)} · sent by ${contact}`,
        tag: "To review",
        tone: "navy" as Tone,
        go: () => go("tasks"),
      })),
    ...overdue.map((t) => ({
      key: `o${t.id}`,
      icon: "hourglass",
      iconTone: "warning" as const,
      title: `Chase: ${t.title}`,
      meta: `${scopeName(t.projectId)} · due ${t.due}`,
      tag: "Overdue",
      tone: "danger" as Tone,
      go: () => go("tasks"),
    })),
    ...pendingRel.map((r) => ({
      key: `s${r.id}`,
      icon: "rocket",
      iconTone: "teal" as const,
      title: `${r.name} sign-off`,
      meta: [
        `Waiting on ${contact}`,
        (r.approveBy ?? r.goLiveAt) &&
          `by ${new Date((r.approveBy ?? r.goLiveAt) as number).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          })}`,
      ]
        .filter(Boolean)
        .join(" · "),
      tag: "Waiting",
      tone: "warning" as Tone,
      go: () => openItem("releases", r.id),
    })),
  ];

  const recent = activity.filter((a) => !inProject || a.projectId === pid).slice(0, 5);
  const editing = editProject ? projects.find((p) => p.id === editProject) : undefined;
  const sub = [
    client?.plan,
    client?.since && `client since ${client.since}`,
    plural(projects.length, "project"),
    client?.contact && `main contact ${client.contact}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="stack-24">
      <PageHead
        eyebrow={`ADMIN · ${client?.name ?? ""}`}
        title={client?.name ?? "Overview"}
        sub={sub}
        actions={
          <>
            {inProject ? (
              <Button variant="secondary" icon="pencil" onClick={() => setEditProject(pid)}>
                Edit project
              </Button>
            ) : client ? (
              <Button variant="secondary" icon="pencil" onClick={() => setEditClient(true)}>
                Edit client
              </Button>
            ) : null}
            <Button icon="plus" onClick={() => setComposing(true)}>
              Log a ticket
            </Button>
          </>
        }
      />
      <ScopeNote pid={pid} setPid={setPid} projectName={projectName} />

      <div className="stat-grid">
        <StatCard
          label="Open tickets"
          value={openT.length}
          note={`${needsReply.length} need${needsReply.length === 1 ? "s" : ""} a reply`}
          hot={needsReply.length > 0}
          onClick={() => go("tickets")}
        />
        <StatCard
          label="Releases in flight"
          value={inFlight.length}
          note={pendingRel.length ? `${pendingRel.length} waiting on sign-off` : "nothing waiting"}
          hot={false}
          onClick={() => go("releases")}
        />
        <StatCard
          label="With the client"
          value={withClient.length}
          note={overdue.length ? `${overdue.length} overdue` : "none overdue"}
          hot={overdue.length > 0}
          bad={overdue.length > 0}
          onClick={() => go("tasks")}
        />
        <StatCard
          label="Change requests"
          value={pendCR.length}
          note={pendCR.length ? `${fmtCost(pendTotal)} awaiting approval` : "nothing pending"}
          hot={false}
          onClick={() => go("changes")}
        />
      </div>

      <div className="grid-2-1">
        <div className="stack-14">
          <div className="sec-row">
            <h2 className="sec-title">Projects</h2>
            <button className="link-btn" onClick={() => go("projects")}>
              All projects →
            </button>
          </div>
          {scopedProjects.length === 0 ? (
            <Card>
              <div className="empty">No projects yet.</div>
            </Card>
          ) : (
            scopedProjects.map((p) => <ProjectCard key={p.id} p={p} full={false} ctx={props} />)
          )}
        </div>

        <div className="stack-14">
          <h2 className="sec-title">Needs attention</h2>
          <div className="list-card">
            {needs.length === 0 ? (
              <div className="list-empty">Nothing waiting on you.</div>
            ) : (
              needs.slice(0, 7).map((n) => (
                <button key={n.key} type="button" className="need-row" onClick={n.go}>
                  <span className={"need-ico is-" + n.iconTone}>
                    <Icon name={n.icon} size={16} />
                  </span>
                  <span className="need-main">
                    <span className="need-title">{n.title}</span>
                    <span className="need-meta mono">{n.meta}</span>
                  </span>
                  <Badge tone={n.tone}>{n.tag}</Badge>
                </button>
              ))
            )}
          </div>
          <div className="sec-row" style={{ marginTop: 8 }}>
            <h2 className="sec-title">Latest activity</h2>
            <button className="link-btn" onClick={() => go("activity")}>
              All activity →
            </button>
          </div>
          <div className="list-card is-padded">
            {recent.length === 0 ? (
              <div className="list-empty">No recent activity.</div>
            ) : (
              recent.map((a) => (
                <div key={a.id} className="act-row">
                  <span className="act-ico">
                    <Icon name={a.icon} size={14} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="act-text">{a.text}</div>
                    <div className="act-meta mono">
                      {scopeName(a.projectId)} · {a.who} · {activityWhen(a.at)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {client ? (
        <ClientEditDialog
          open={editClient}
          client={client}
          onClose={() => setEditClient(false)}
          onSaved={() => {
            reload();
            toast("Client updated.");
          }}
        />
      ) : null}
      {editing ? (
        <ProjectEditDialog
          open
          project={editing}
          onClose={() => setEditProject(null)}
          onSaved={() => {
            reload();
            toast("Project updated.");
          }}
        />
      ) : null}
      <NewTicket
        open={composing}
        isAdmin
        projects={projects}
        defaultPid={pid}
        onClose={() => setComposing(false)}
        onCreated={(id) => {
          reload();
          toast(`Ticket #${id8(id)} logged.`);
          go("tickets");
        }}
      />
    </div>
  );
}

/* ---------- client Home (handoff C1 `isCHome`) ---------- */

function ClientHome(props: PageProps) {
  const { projects, milestones, releases, activity, me, pid, go, openItem, reload, toast } = props;
  const [composing, setComposing] = useState(false);
  const todo = buildTodo(props);
  const n = todo.length;

  const open = (t: TodoItem) => {
    if (t.kind === "reply" && t.ticket) openItem("tickets", t.ticket.id);
    else if (t.kind === "release" && t.release) openItem("releases", t.release.id);
    else go("tasks");
  };
  const updates = activity.filter((a) => !a.internal).slice(0, 4);

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="HOME"
        title={`Hello, ${firstName(me.name) || "there"}.`}
        sub={
          n
            ? `${plural(n, "thing")} need${n === 1 ? "s" : ""} you. Everything else is with us.`
            : "Nothing needs you right now. Everything is with us."
        }
        actions={
          <Button icon="plus" onClick={() => setComposing(true)}>
            New ticket
          </Button>
        }
      />

      <div className="ch-needs">
        <span className="ch-needs-rule" />
        <div className="ch-needs-head">Needs you</div>
        {todo.slice(0, 4).map((t) => (
          <button key={t.key} type="button" className="ch-need" onClick={() => open(t)}>
            <span className="ch-need-ico">
              <Icon name={t.icon} size={18} />
            </span>
            <span className="ch-need-main">
              <span className="ch-need-title">{t.title}</span>
              {t.meta ? <span className="ch-need-meta">{t.meta}</span> : null}
            </span>
            <Badge tone={t.overdue ? "danger" : "neutral"}>{t.overdue ? "Overdue" : t.label}</Badge>
            <span className="ch-need-chev">
              <Icon name="chevron-right" size={16} />
            </span>
          </button>
        ))}
        {n === 0 ? (
          <div className="ch-needs-empty">Nothing right now. We'll email you if that changes.</div>
        ) : null}
        {n > 4 ? (
          <button type="button" className="ch-needs-more" onClick={() => go("tasks")}>
            See all {n} in To do →
          </button>
        ) : null}
      </div>

      <h2 className="sec-title">Your projects</h2>
      {projects.length === 0 ? (
        <div className="list-card">
          <div className="list-empty">No projects yet.</div>
        </div>
      ) : (
        <div className="ch-projects">
          {projects.map((p) => {
            const ms = projectMilestones(milestones, p.id);
            const nextM = ms.find((m) => m.state === "active") ?? ms.find((m) => m.state === "next");
            const r = currentRelease(releases, p.id);
            const behind = p.health === "at-risk";
            const release = r
              ? `${r.name} · ${
                  r.status === "On sandbox"
                    ? "ready for you to test"
                    : r.goLiveAt
                      ? `goes live ${fmtDay(r.goLiveAt)}`
                      : "date to be set"
                }`
              : "None planned";
            return (
              <div key={p.id} className="ch-proj">
                <div className="ch-proj-head">
                  <span className="ch-proj-name">{p.name}</span>
                  <span className="ch-proj-status">
                    <span className={"ch-dot" + (behind ? " is-behind" : "")} />
                    {behind ? "Running a little behind" : "On track"}
                  </span>
                </div>
                <Progress pct={p.progress} />
                <div className="ch-proj-facts">
                  <span className="ch-proj-k">Next up</span>
                  <span>
                    {nextM ? [nextM.name, fmtDay(nextM.dateAt)].filter(Boolean).join(" · ") : "Nothing planned"}
                  </span>
                  <span className="ch-proj-k">Release</span>
                  <span>{release}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h2 className="sec-title">Recent updates</h2>
      <div className="list-card">
        {updates.length === 0 ? (
          <div className="list-empty">Nothing yet. Updates on your work show up here.</div>
        ) : (
          updates.map((a) => {
            const day = activityDay(a.at);
            return (
              <div key={a.id} className="ch-update">
                <span className="ch-update-text">{a.text}</span>
                <span className="ch-update-when mono">{day === "Today" ? activityWhen(a.at) : day}</span>
              </div>
            );
          })
        )}
      </div>

      <NewTicket
        open={composing}
        isAdmin={false}
        projects={projects}
        defaultPid={pid}
        onClose={() => setComposing(false)}
        onCreated={(id) => {
          reload();
          toast("Ticket raised. You'll get a reply within one working day.");
          openItem("tickets", id);
        }}
      />
    </div>
  );
}
